import axiosInstance from '../helpers/axios'
import guestApi from '../helpers/guestApi'

const PIN_ERROR_CODES = new Set([
    'SCENE_GUEST_PIN_REQUIRED',
    'SCENE_GUEST_PIN_INVALID',
    'PIN_REQUIRED',
    'PIN_INVALID',
])

const getError = (error, fallback = 'Ошибка гостевого доступа к сцене') => {
    return error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
}

const getErrorCode = (error) => String(
    error?.response?.data?.code ||
    error?.code ||
    ''
).trim().toUpperCase()

const isPinRequiredError = (error) => {
    const responseData = error?.response?.data || {}
    const code = getErrorCode(error)
    const status = Number(error?.response?.status || 0)
    const message = String(
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        ''
    ).toLowerCase()

    if (responseData?.requiresPin === true) return true
    if (PIN_ERROR_CODES.has(code)) return true

    return (
        [400, 401, 403].includes(status) &&
        /\bpin\b|пин/.test(message)
    )
}

const isTimeoutError = (error) => {
    const code = getErrorCode(error)
    const message = String(error?.message || '').toLowerCase()

    return code === 'ECONNABORTED' ||
        code === 'ETIMEDOUT' ||
        message.includes('timeout')
}

const buildResult = (response) => ({
    ok: true,
    data: response.data,
})

const buildErrorResult = (error, fallback) => ({
    ok: false,
    error: getError(error, fallback),
    code: getErrorCode(error),
    status: Number(error?.response?.status || 0),
    requiresPin: isPinRequiredError(error),
    timedOut: isTimeoutError(error),
    networkError: !error?.response,
})


export const resolveAnySceneAccessCode = (accessKey) => {
    return async () => {
        try {
            const response = await guestApi.post(
                '/scene-guest-access/resolve-code',
                { accessKey: String(accessKey || '').trim() },
                { timeout: 15000 }
            )

            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Код доступа не найден')
        }
    }
}

export const createSceneGuestAccess = (payload = {}) => {
    return async () => {
        try {
            const response = await axiosInstance.post('/scene-guest-access', payload)
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось создать гостевой доступ')
        }
    }
}

export const listSceneGuestAccesses = (officeId, params = {}) => {
    return async () => {
        try {
            const response = await axiosInstance.get(`/scene-guest-access/office/${officeId}`, {
                params,
            })
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить гостевые доступы')
        }
    }
}

export const getSceneGuestAccessOptions = (officeId, sessionId) => {
    return async () => {
        try {
            const response = await axiosInstance.get(
                `/scene-guest-access/options/${officeId}/${encodeURIComponent(sessionId)}`
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось подготовить параметры гостевого доступа')
        }
    }
}

export const updateSceneGuestAccess = (grantId, patch = {}) => {
    return async () => {
        try {
            const response = await axiosInstance.patch(`/scene-guest-access/${grantId}`, {
                patch,
            })
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось обновить гостевой доступ')
        }
    }
}

export const revokeSceneGuestAccess = (grantId) => {
    return async () => {
        try {
            const response = await axiosInstance.post(`/scene-guest-access/${grantId}/revoke`)
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось деактивировать гостевой доступ')
        }
    }
}

export const deleteSceneGuestAccess = (grantId) => {
    return async () => {
        try {
            const response = await axiosInstance.delete(`/scene-guest-access/${grantId}`)
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось удалить гостевой доступ')
        }
    }
}

export const resolveSceneGuestAccess = ({
    accessKey,
    pin = '',
    timeoutMs = 15000,
}) => {
    return async () => {
        try {
            const response = await guestApi.post(
                '/scene-guest-access/resolve',
                {
                    accessKey,
                    pin,
                },
                {
                    timeout: Math.max(5000, Number(timeoutMs) || 15000),
                }
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось открыть гостевую сцену')
        }
    }
}

export const getSceneGuestView = (guestToken) => {
    return async () => {
        try {
            const response = await guestApi.get('/scene-guest-access/view', {
                sceneGuestToken: guestToken,
            })
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить гостевую сцену')
        }
    }
}

export const getSceneGuestVersion = (guestToken) => {
    return async () => {
        try {
            const response = await guestApi.get('/scene-guest-access/version', {
                sceneGuestToken: guestToken,
            })
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось проверить версию сцены')
        }
    }
}

export const getSceneGuestCatalog = (guestToken, params = {}) => {
    return async () => {
        try {
            const response = await guestApi.get('/scene-guest-access/catalog', {
                params,
                sceneGuestToken: guestToken,
            })
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить каталог')
        }
    }
}

export const claimSceneGuestAccess = (guestToken) => {
    return async () => {
        try {
            const response = await axiosInstance.post(
                '/scene-guest-access/claim',
                { guestToken }
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось сохранить проект')
        }
    }
}

export const listMySceneGuestAccesses = () => {
    return async () => {
        try {
            const response = await axiosInstance.get('/scene-guest-access/my')
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить сохранённые проекты')
        }
    }
}

export const openMySceneGuestAccess = (grantId) => {
    return async () => {
        try {
            const response = await axiosInstance.post(`/scene-guest-access/my/${grantId}/open`)
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось открыть сохранённый проект')
        }
    }
}
export const getSceneGuestAccessCredentials = (grantId) => {
    return async () => {
        try {
            const response = await axiosInstance.get(
                `/scene-guest-access/${grantId}/credentials`
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить реквизиты доступа')
        }
    }
}

export const registerSceneGuestVisitor = (guestToken, payload = {}) => {
    return async () => {
        try {
            const response = await guestApi.post(
                '/scene-guest-access/visitor',
                payload,
                { sceneGuestToken: guestToken }
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось зарегистрировать посещение сцены')
        }
    }
}

export const updateSceneGuestVisitorName = (guestToken, name) => {
    return async () => {
        try {
            const response = await guestApi.patch(
                '/scene-guest-access/visitor/name',
                { name },
                { sceneGuestToken: guestToken }
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось изменить имя посетителя')
        }
    }
}

export const getSceneGuestChatMessages = (guestToken, params = {}) => {
    return async () => {
        try {
            const response = await guestApi.get('/scene-guest-access/chat/messages', {
                params,
                sceneGuestToken: guestToken,
            })
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить сообщения')
        }
    }
}

export const sendSceneGuestChatMessage = (guestToken, payload = {}) => {
    return async () => {
        try {
            let body = payload

            if (payload?.file) {
                body = new FormData()
                body.append('text', payload.text || '')
                body.append('file', payload.file)
            }

            const response = await guestApi.post(
                '/scene-guest-access/chat/messages',
                body,
                { sceneGuestToken: guestToken }
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось отправить сообщение')
        }
    }
}

export const markSceneGuestChatRead = (guestToken) => {
    return async () => {
        try {
            const response = await guestApi.post(
                '/scene-guest-access/chat/read',
                {},
                { sceneGuestToken: guestToken }
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось отметить сообщения прочитанными')
        }
    }
}

export const getSceneGuestAccessActivity = (grantId) => {
    return async () => {
        try {
            const response = await axiosInstance.get(
                `/scene-guest-access/${grantId}/activity`
            )
            return buildResult(response)
        } catch (error) {
            return buildErrorResult(error, 'Не удалось загрузить активность заказчиков')
        }
    }
}
